import { useEffect, useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { DemoBookingCard, DemoBooking } from './DemoBookingCard';
import { formatTime } from '@/lib/dateFormat';

// ────────────────────────────────────────────────────────────────────────────
// Demo availability Calendar view (read-only) — mirrors the tenant Time
// Availability calendar: a month grid showing each day's available hours
// (weekly + date overrides) and any booked demos.
// ────────────────────────────────────────────────────────────────────────────

interface Range { start: string; end: string }
interface Override { id: string; date: string; ranges: Range[] }
interface Cfg {
  workingDays: number[];
  weeklyHours: Record<string, Range[]>;
  startTime: string;
  endTime: string;
  overrides?: Override[];
}
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const fmt12 = (t: string) => { const [h, mi] = t.split(':').map(Number); const ap = h < 12 ? 'a' : 'p'; return `${h % 12 || 12}:${pad(mi)}${ap}`; };

export function SuperAdminDemoCalendar({ cfg, apiBase = '/super-admin/demo' }: { cfg: Cfg; apiBase?: string }) {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [bookings, setBookings] = useState<DemoBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DemoBooking | null>(null);

  const load = useCallback(() => {
    saApi.get(`${apiBase}/bookings`, { params: { scope: 'all' } })
      .then(({ data }) => setBookings(Array.isArray(data) ? data : []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const overridesByDate = useMemo(() => {
    const m: Record<string, Range[]> = {};
    (cfg.overrides ?? []).forEach(o => { m[o.date] = o.ranges; });
    return m;
  }, [cfg.overrides]);

  const bookingsByDate = useMemo(() => {
    const m: Record<string, DemoBooking[]> = {};
    bookings.filter(b => b.status === 'confirmed').forEach(b => {
      const d = new Date(b.startAt);
      const key = ymd(d.getFullYear(), d.getMonth(), d.getDate());
      (m[key] ??= []).push(b);
    });
    return m;
  }, [bookings]);

  const rangesFor = (dateStr: string, weekday: number): { ranges: Range[]; override: boolean } => {
    if (dateStr in overridesByDate) return { ranges: overridesByDate[dateStr], override: true };
    if (!cfg.workingDays.includes(weekday)) return { ranges: [], override: false };
    const wh = cfg.weeklyHours?.[String(weekday)];
    return { ranges: wh?.length ? wh : [{ start: cfg.startTime, end: cfg.endTime }], override: false };
  };

  const firstDow = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayKey = ymd(now.getFullYear(), now.getMonth(), now.getDate());

  const move = (delta: number) => setCursor(c => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-800">{MONTHS[cursor.m]} {cursor.y}</h3>
        <div className="flex gap-1">
          <button onClick={() => move(-1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="h-4 w-4 text-slate-500" /></button>
          <button onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })} className="px-3 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-600">Today</button>
          <button onClick={() => move(1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="h-4 w-4 text-slate-500" /></button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WD.map(d => <div key={d} className="text-[10px] font-semibold text-slate-400 uppercase text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (day === null) return <div key={i} />;
              const key = ymd(cursor.y, cursor.m, day);
              const weekday = new Date(cursor.y, cursor.m, day).getDay();
              const { ranges, override } = rangesFor(key, weekday);
              const dayBookings = bookingsByDate[key] ?? [];
              const available = ranges.length > 0;
              const isToday = key === todayKey;
              return (
                <div key={i} className={`min-h-[74px] rounded-lg border p-1.5 ${available ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/60'} ${isToday ? 'ring-1 ring-[#6366F1]' : ''}`}>
                  <div className={`text-[11px] font-bold ${isToday ? 'text-[#6366F1]' : available ? 'text-slate-700' : 'text-slate-400'}`}>{day}</div>
                  {override && <div className="text-[9px] text-violet-500 font-semibold">override</div>}
                  {available ? (
                    <div className="mt-0.5 space-y-0.5">
                      {ranges.slice(0, 2).map((r, ri) => (
                        <div key={ri} className="text-[9px] leading-tight text-emerald-600 truncate">{fmt12(r.start)}–{fmt12(r.end)}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[9px] text-slate-300 mt-0.5">{key in overridesByDate ? 'Unavailable' : ''}</div>
                  )}
                  {dayBookings.slice(0, 2).map(b => (
                    <button key={b.id} onClick={() => setSelected(b)}
                      className="block w-full text-left mt-0.5 text-[9px] leading-tight bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded px-1 py-0.5 truncate" title={`${b.guestName} — view details`}>
                      {formatTime(b.startAt)} {b.guestName}
                    </button>
                  ))}
                  {dayBookings.length > 2 && <div className="text-[9px] text-slate-400">+{dayBookings.length - 2} more</div>}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Available</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-400" /> Booked demo</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400" /> Date override</span>
          </div>
        </>
      )}

      {/* Booking detail popup — guest info, Zoom link + full lifecycle actions. */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <div className="relative z-10 w-full max-w-lg">
            <div className="flex justify-end mb-2">
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg bg-white/90 hover:bg-white shadow">
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <DemoBookingCard
              booking={selected}
              onChange={() => { load(); setSelected(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
